/**
 * ThemeProvider — Applies the active theme (mode + org branding) to the DOM.
 *
 * Sets `data-mode` on <html> for CSS variable switching.
 * Applies org brand overrides as inline CSS custom properties.
 * Listens for system preference changes when mode is "system".
 */
import { useEffect } from 'react';
import { useThemeStore } from '../stores/themeStore';
import type { OrgThemeConfig } from '../stores/themeStore';

function hexToRgb(hex: string): string | null {
  const cleaned = hex.replace('#', '');
  if (cleaned.length !== 6 && cleaned.length !== 3) return null;
  const full = cleaned.length === 3
    ? cleaned.split('').map(c => c + c).join('')
    : cleaned;
  const r = parseInt(full.slice(0, 2), 16);
  const g = parseInt(full.slice(2, 4), 16);
  const b = parseInt(full.slice(4, 6), 16);
  if (isNaN(r) || isNaN(g) || isNaN(b)) return null;
  return `${r} ${g} ${b}`;
}

function generateBrandShades(rgb: string) {
  const [r, g, b] = rgb.split(' ').map(Number);
  const lighten = (v: number, pct: number) => Math.min(255, Math.round(v + (255 - v) * pct));
  const darken = (v: number, pct: number) => Math.round(v * (1 - pct));

  return {
    hover: `${lighten(r, 0.12)} ${lighten(g, 0.12)} ${lighten(b, 0.12)}`,
    light: `${lighten(r, 0.3)} ${lighten(g, 0.3)} ${lighten(b, 0.3)}`,
    dark: `${darken(r, 0.2)} ${darken(g, 0.2)} ${darken(b, 0.2)}`,
  };
}

function applyOrgTheme(el: HTMLElement, theme: OrgThemeConfig | null) {
  const props = ['--brand', '--brand-hover', '--brand-light', '--brand-dark', '--brand-secondary'];
  const statusProps = ['--status-success', '--status-warning', '--status-error', '--status-info'];

  // Clear any previously set org overrides
  [...props, ...statusProps].forEach(p => el.style.removeProperty(p));

  if (!theme) return;

  if (theme.primary_color) {
    const rgb = hexToRgb(theme.primary_color);
    if (rgb) {
      const shades = generateBrandShades(rgb);
      el.style.setProperty('--brand', rgb);
      el.style.setProperty('--brand-hover', shades.hover);
      el.style.setProperty('--brand-light', shades.light);
      el.style.setProperty('--brand-dark', shades.dark);
    }
  }

  if (theme.secondary_color) {
    const rgb = hexToRgb(theme.secondary_color);
    if (rgb) el.style.setProperty('--brand-secondary', rgb);
  }

  if (theme.status_overrides) {
    for (const [key, hex] of Object.entries(theme.status_overrides)) {
      const rgb = hexToRgb(hex);
      if (rgb) el.style.setProperty(`--status-${key}`, rgb);
    }
  }
}

export function ThemeProvider({ children }: { children: React.ReactNode }) {
  const { resolvedMode, mode, orgTheme, setMode } = useThemeStore();

  // Apply data-mode to <html>
  useEffect(() => {
    document.documentElement.setAttribute('data-mode', resolvedMode);
  }, [resolvedMode]);

  // Apply org brand overrides
  useEffect(() => {
    applyOrgTheme(document.documentElement, orgTheme);
  }, [orgTheme]);

  // Listen for system preference changes
  useEffect(() => {
    if (mode !== 'system') return;
    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    const handler = () => setMode('system');
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, [mode, setMode]);

  return <>{children}</>;
}
